<template>
  <div class="card">
    <div class="card-body">
      <h5 class="card-title mb-4">
        <i class="bx bx-calculator text-primary font-size-20 align-middle mr-2"></i>
        Simulador de Vendas
      </h5>

      <div class="row">
        <!-- Inputs -->
        <div class="col-md-5">
          <!-- Valor -->
          <div class="form-group">
            <label class="font-weight-medium">Valor do ingresso</label>
            <div class="input-group">
              <div class="input-group-prepend">
                <span class="input-group-text">R$</span>
              </div>
              <input
                type="number"
                step="0.01"
                min="1"
                class="form-control form-control-lg"
                v-model.number="amount"
                placeholder="100.00"
              >
            </div>
          </div>

          <!-- Método -->
          <div class="form-group">
            <label class="font-weight-medium">Método de pagamento</label>
            <div class="btn-group btn-group-toggle d-flex" data-toggle="buttons">
              <label
                class="btn btn-outline-primary flex-fill"
                :class="{ active: method === 'pix' }"
                @click="method = 'pix'"
              >
                <i class="bx bx-qr mr-1"></i> PIX
              </label>
              <label
                class="btn btn-outline-primary flex-fill"
                :class="{ active: method === 'card' }"
                @click="method = 'card'"
              >
                <i class="bx bx-credit-card mr-1"></i> Cartão
              </label>
              <label
                class="btn btn-outline-primary flex-fill"
                :class="{ active: method === 'boleto' }"
                @click="method = 'boleto'"
              >
                <i class="bx bx-barcode mr-1"></i> Boleto
              </label>
            </div>
          </div>

          <!-- Parcelas -->
          <div class="form-group" v-if="method === 'card'">
            <label class="font-weight-medium">Parcelas</label>
            <select v-model.number="installments" class="form-control">
              <option v-for="n in maxInstallments" :key="n" :value="n">{{ n }}x</option>
            </select>
          </div>

          <!-- Detalhes das taxas -->
          <div v-if="amount > 0" class="p-3 rounded border" style="background: #f9fafb;">
            <h6 class="text-muted font-size-12 text-uppercase mb-3">
              <i class="bx bx-detail mr-1"></i> Composição das taxas
            </h6>
            <div class="d-flex justify-content-between mb-1 font-size-13">
              <span>{{ methodLabel }}</span>
              <span>{{ methodFeeDisplay }}</span>
            </div>
            <div class="d-flex justify-content-between mb-1 font-size-13">
              <span>Taxa por transação</span>
              <span>{{ formatCurrency(fees.transaction_fee_fixed) }}</span>
            </div>
            <div v-if="method === 'card'" class="d-flex justify-content-between mb-1 font-size-13">
              <span>Antifraude</span>
              <span>{{ formatCurrency(fees.antifraud_fee_fixed) }}</span>
            </div>
            <hr class="my-2">
            <div class="d-flex justify-content-between font-size-13 font-weight-bold">
              <span>Total de taxas</span>
              <span class="text-danger">{{ formatCurrency(totalFee) }}</span>
            </div>
          </div>

          <!-- Prazo -->
          <div v-if="amount > 0" class="mt-3 p-2 rounded border d-flex align-items-center" style="background: #eff6ff;">
            <i class="bx bx-time-five text-primary font-size-18 mr-2"></i>
            <span class="font-size-13">
              <strong>Prazo:</strong> {{ receivePeriod }}
            </span>
          </div>
        </div>

        <!-- Resultado -->
        <div class="col-md-7" v-if="amount > 0">
          <div class="row">
            <!-- Cenário: Repassar taxa ao cliente -->
            <div class="col-12 mb-3">
              <div class="p-3 rounded border-left border-warning" style="background: #fffbeb; border-left-width: 4px !important;">
                <h6 class="mb-3">
                  <i class="bx bx-user text-warning mr-1"></i>
                  Repassando taxa para o cliente
                </h6>

                <div class="d-flex justify-content-between mb-1 font-size-13">
                  <span>Valor do ingresso</span>
                  <span>{{ formatCurrency(amount) }}</span>
                </div>
                <div class="d-flex justify-content-between mb-1 font-size-13 text-warning">
                  <span>+ Taxa de serviço</span>
                  <span>{{ formatCurrency(totalFee) }}</span>
                </div>
                <hr class="my-2">
                <div class="d-flex justify-content-between mb-1">
                  <strong>Cliente pagará</strong>
                  <strong class="text-dark font-size-16">{{ formatCurrency(amount + totalFee) }}</strong>
                </div>
                <div v-if="method === 'card' && installments > 1" class="d-flex justify-content-between font-size-12 text-muted">
                  <span></span>
                  <span>{{ installments }}x de {{ formatCurrency((amount + totalFee) / installments) }}</span>
                </div>
                <div class="d-flex justify-content-between mt-2 pt-2 border-top">
                  <strong class="text-success">Você recebe</strong>
                  <strong class="text-success font-size-16">{{ formatCurrency(amount) }}</strong>
                </div>
              </div>
            </div>

            <!-- Cenário: Assumir taxa -->
            <div class="col-12">
              <div class="p-3 rounded border-left border-primary" style="background: #eff6ff; border-left-width: 4px !important;">
                <h6 class="mb-3">
                  <i class="bx bx-building text-primary mr-1"></i>
                  Você assumindo a taxa
                </h6>

                <div class="d-flex justify-content-between mb-1 font-size-13">
                  <span>Valor do ingresso</span>
                  <span>{{ formatCurrency(amount) }}</span>
                </div>
                <div class="d-flex justify-content-between mb-1 font-size-13 text-danger">
                  <span>- Taxas Pagar.me</span>
                  <span>{{ formatCurrency(totalFee) }}</span>
                </div>
                <hr class="my-2">
                <div class="d-flex justify-content-between mb-1">
                  <strong>Cliente pagará</strong>
                  <strong class="text-dark font-size-16">{{ formatCurrency(amount) }}</strong>
                </div>
                <div v-if="method === 'card' && installments > 1" class="d-flex justify-content-between font-size-12 text-muted">
                  <span></span>
                  <span>{{ installments }}x de {{ formatCurrency(amount / installments) }}</span>
                </div>
                <div class="d-flex justify-content-between mt-2 pt-2 border-top">
                  <strong class="text-success">Você recebe</strong>
                  <strong class="text-success font-size-16">{{ formatCurrency(amount - totalFee) }}</strong>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'PaymentSimulator',
  props: {
    maxInstallments: {
      type: Number,
      default: 12
    }
  },
  data () {
    return {
      amount: 100,
      method: 'pix',
      installments: 1,
      fees: {
        card_fee_1x: 3.19,
        card_fee_2_6x: 4.49,
        card_fee_7_12x: 4.99,
        pix_fee_percentage: 1.09,
        boleto_fee_fixed: 3.19,
        transaction_fee_fixed: 0.55,
        antifraud_fee_fixed: 0.44
      }
    }
  },
  computed: {
    cardFeePercent () {
      if (this.installments <= 1) return this.fees.card_fee_1x
      if (this.installments <= 6) return this.fees.card_fee_2_6x
      return this.fees.card_fee_7_12x
    },
    methodLabel () {
      if (this.method === 'pix') return `PIX (${this.fees.pix_fee_percentage}%)`
      if (this.method === 'boleto') return 'Boleto (fixo)'
      return `Cartão (${this.cardFeePercent}%)`
    },
    // Fórmula inversa: total = (base + fixas) / (1 - %/100)
    // Garante que líquido = valor base após Pagar.me descontar as taxas
    totalWithFee () {
      const base = this.amount
      const txFee = this.fees.transaction_fee_fixed

      if (this.method === 'pix') {
        return (base + txFee) / (1 - this.fees.pix_fee_percentage / 100)
      }
      if (this.method === 'boleto') {
        return base + this.fees.boleto_fee_fixed + txFee
      }
      // card
      const antifraud = this.fees.antifraud_fee_fixed
      return (base + txFee + antifraud) / (1 - this.cardFeePercent / 100)
    },
    totalFee () {
      return this.totalWithFee - this.amount
    },
    methodFeeDisplay () {
      // Mostra a parte percentual calculada sobre o total (como Pagar.me cobra)
      if (this.method === 'pix') return this.formatCurrency(this.totalWithFee * this.fees.pix_fee_percentage / 100)
      if (this.method === 'boleto') return this.formatCurrency(this.fees.boleto_fee_fixed)
      return this.formatCurrency(this.totalWithFee * this.cardFeePercent / 100)
    },
    receivePeriod () {
      if (this.method === 'pix') return 'Instantâneo'
      if (this.method === 'boleto') return 'D+2 (2 dias úteis)'
      return 'D+30 (30 dias)'
    }
  },
  watch: {
    method () {
      this.installments = 1
    }
  },
  methods: {
    formatCurrency (value) {
      return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL'
      }).format(value)
    }
  }
}
</script>

<style scoped>
.btn-group-toggle .btn.active {
  background-color: #556ee6;
  color: #fff;
  border-color: #556ee6;
}
</style>
